import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, TrendingDown, PiggyBank, HandCoins, Package, Coins, BarChart3, AlertTriangle, Calendar, X } from "lucide-react";

const COLORS = ["#C6296B", "#E8A22D", "#4A9E88", "#D95757", "#8B5CF6", "#0EA5E9"];

export default function Financials() {
  const { user } = useAuth();
  const { format, currency, rate } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/financials", { params });
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    load(dateFrom, dateTo);
  }, [user]); // eslint-disable-line

  const applyFilter = () => load(dateFrom, dateTo);
  const clearFilter = () => { setDateFrom(""); setDateTo(""); load("", ""); };

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  // Convert CVE amounts to display currency for charts
  const toDisplay = (cve) => currency === "EUR" ? cve / (rate || 1) : cve;

  const monthly = (() => {
    const map = new Map();
    (data?.sales_by_month || []).forEach((m) => {
      map.set(m.month, {
        month: m.month,
        Receita: Math.round(toDisplay(m.revenue) * 100) / 100,
        "Lucro bruto": Math.round(toDisplay(m.profit) * 100) / 100,
        Despesas: 0,
      });
    });
    (data?.expenses_by_month || []).forEach((m) => {
      const existing = map.get(m.month) || { month: m.month, Receita: 0, "Lucro bruto": 0, Despesas: 0 };
      existing.Despesas = Math.round(toDisplay(m.amount) * 100) / 100;
      map.set(m.month, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  })();

  const byCat = (data?.profit_by_category || []).map((c) => ({
    name: c.category,
    value: Math.round(toDisplay(c.profit) * 100) / 100,
  })).filter((x) => x.value > 0);

  return (
    <div className="min-h-screen bg-background" data-testid="financials-page">
      <DashboardHeader title="Painel financeiro" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Financeiro</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Visão geral</h1>
          <p className="text-sm text-muted-foreground mt-1">Valores em {currency === "EUR" ? "€" : "CVE"} · toggle disponível no topo.</p>
        </div>

        {/* Date range filter */}
        <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-semibold">Intervalo de datas</span>
            {(dateFrom || dateTo) && (
              <span className="ml-2 text-xs text-muted-foreground">
                {dateFrom || "início"} → {dateTo || "hoje"}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 sm:items-end">
            <div className="space-y-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="fin-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="fin-date-to" />
            </div>
            <Button onClick={applyFilter} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white h-10" data-testid="fin-apply-filter">Aplicar</Button>
            <Button variant="outline" onClick={clearFilter} className="h-10" data-testid="fin-clear-filter"><X className="w-4 h-4 mr-1" /> Limpar</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <QuickRange label="Este mês" from={firstDayOfMonth()} to={today()} setFrom={setDateFrom} setTo={setDateTo} onApply={load} />
            <QuickRange label="Mês passado" {...lastMonth()} setFrom={setDateFrom} setTo={setDateTo} onApply={load} />
            <QuickRange label="Últimos 30 dias" from={daysAgo(30)} to={today()} setFrom={setDateFrom} setTo={setDateTo} onApply={load} />
            <QuickRange label="Este ano" from={firstDayOfYear()} to={today()} setFrom={setDateFrom} setTo={setDateTo} onApply={load} />
          </div>
        </section>

        {loading || !data ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{Array.from({length:4}).map((_,i)=><div key={i} className="h-24 bg-white rounded-2xl lb-shadow-card animate-pulse" />)}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <KpiCard icon={<PiggyBank className="w-4 h-4" />} label="Total investido" value={format(data.total_invested)} sub="Custo total do stock (atual + vendido)" />
              <KpiCard icon={<TrendingUp className="w-4 h-4 text-[hsl(var(--primary))]" />} label="Lucro bruto realizado" value={format(data.profit_realized)} sub={`${data.sales_count} venda(s)`} accent />
              <KpiCard icon={<TrendingDown className="w-4 h-4 text-[hsl(var(--destructive))]" />} label="Despesas (envio, alfândega…)" value={format(data.total_expenses || 0)} sub="Descontadas do lucro líquido" danger />
              <KpiCard icon={<Coins className="w-4 h-4 text-[hsl(var(--primary))]" />} label="Lucro líquido" value={format(data.profit_net || 0)} sub="Bruto − despesas" accent />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <KpiCard icon={<Package className="w-4 h-4" />} label="Lucro potencial (stock)" value={format(data.profit_projected_stock)} sub="Se todo o stock atual for vendido" />
              <KpiCard icon={<HandCoins className="w-4 h-4 text-[hsl(var(--destructive))]" />} label="Fiado em aberto" value={format(data.total_fiado_open)} sub="A receber de clientes" danger />
              {data.total_losses > 0 && (
                <KpiCard icon={<AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))]" />} label="Quebras / perdas" value={format(data.total_losses)} sub={`${data.losses_qty} un. descartadas`} danger />
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-4 h-4" />
                  <h2 className="font-display text-lg">Vendas por mês</h2>
                </div>
                {monthly.length === 0 ? (
                  <EmptyChart label="Sem vendas registadas ainda" />
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => format(currency === "EUR" ? v * (rate || 1) : v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Receita" fill="#E8A22D" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Lucro bruto" fill="#C6296B" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Despesas" fill="#D14545" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </section>

              <section className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Coins className="w-4 h-4" />
                  <h2 className="font-display text-lg">Lucro por categoria</h2>
                </div>
                {byCat.length === 0 ? (
                  <EmptyChart label="Sem dados por categoria" />
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={byCat} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {byCat.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => format(currency === "EUR" ? v * (rate || 1) : v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, danger, accent }) {
  return (
    <div className={`rounded-2xl p-4 bg-white lb-shadow-card ${danger ? "ring-1 ring-[hsl(var(--destructive))]/30" : ""}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-widest">{icon}{label}</div>
      <div className={`mt-1 font-display text-2xl ${accent ? "text-[hsl(var(--primary))]" : danger ? "text-[hsl(var(--destructive))]" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">{label}</div>;
}

// --- date helpers ---
function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
function firstDayOfMonth() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }
function firstDayOfYear() { const d = new Date(); d.setMonth(0, 1); return d.toISOString().slice(0, 10); }
function lastMonth() {
  const d = new Date();
  d.setDate(1);
  const to = new Date(d.getTime() - 24 * 3600 * 1000);
  const from = new Date(to);
  from.setDate(1);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function QuickRange({ label, from, to, setFrom, setTo, onApply }) {
  return (
    <button
      type="button"
      onClick={() => { setFrom(from); setTo(to); onApply(from, to); }}
      className="px-3 py-1 rounded-full text-xs font-semibold border border-border bg-white hover:border-[hsl(var(--primary))]"
      data-testid={`quick-range-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {label}
    </button>
  );
}
